import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Grid,
    Icon,
    IconButton,
    InputAdornment,
    Radio,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import CustomSkeleton from "@/components/skeleton/customSkeleton";
import CustomSelect from "@/components/select/select";
import {DatePicker, DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import PhoneInput, {CountryData} from "react-phone-input-2";
import 'react-phone-input-2/lib/material.css';
import React, {useRef, useState} from "react";
import {SafeHtmlRenderer} from "@/components/form/form";
import Link from "next/link";
import LoadingButton from "@mui/lab/LoadingButton";
import Error from "@/components/shared/error";
import moment from "moment";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {TFunction} from "i18next";
import {FORM_ITEM, FORM_ITEMS, GENERAL_OBJECT} from "@/components/types";
import SafeHtml from "@/components/snackbar/safeHTML";
import {SERVER_ERROR_PROPS} from "@/components/admin/login/types";


const button = {
    "& .material-icons": {
        fontSize: "0.8rem!important"
    }
}

const isValidDate = (date: string | Date) => {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
};

const isDateIntervalValid = (item: FORM_ITEM, value: string | Date): {
    code: number;
    message?: string;
    localesData?: { [key: string]: any }
} => {
    if (item.validationData?.type === "yearsInPast") {
        const yearsAgo = moment().subtract(item.validationData.numberOfYears, 'year');
        if (!moment(new Date(value)).isBefore(yearsAgo)) {
            return {
                code: 500,
                message: "dateMustBeInThePastYears",
                localesData: {
                    year: item.validationData.numberOfYears
                }
            }
        }
    }
    return {
        code: 200
    }
}

interface CUSTOM_FORM_NEW_PROPS {
    t: TFunction,
    loading?: boolean;
    apiLoading?: boolean;
    formItems: FORM_ITEMS[];
    onValidSubmit?: (a: { [key: string]: any }) => void;
    globalLabelStyles?: { [key: string]: any };
    globalStyles?: { [key: string]: any };
    formItemGlobalStyles?: { [key: string]: any };
    cancelButton?: { [key: string]: any };
    error?: SERVER_ERROR_PROPS | null;
    hidden?: any[];
    isForFilters?: boolean;
    isForDialog?: boolean;
    buttonStyles?: { [key: string]: any };
    values: GENERAL_OBJECT;
    setValues: React.Dispatch<React.SetStateAction<GENERAL_OBJECT>>;
    noSubmitButton?: boolean;
    customButtons?: any[];
    disableSubmitButton?: boolean
}

const CustomFormNew = ({
                           t,
                           loading = false,
                           apiLoading = false,
                           formItems = [],
                           onValidSubmit,
                           globalLabelStyles = {},
                           globalStyles = {},
                           formItemGlobalStyles = {},
                           cancelButton = {},
                           error = null,
                           hidden = [],
                           isForFilters = false,
                           buttonStyles = {},
                           values,
                           setValues,
                           noSubmitButton = false,
                           customButtons = [],
                           disableSubmitButton = false,
                           isForDialog = false
                       }: CUSTOM_FORM_NEW_PROPS) => {
    const theme = useTheme();
    const [errorItems, setErrorItems] = useState<string[]>([]);
    const [errorItemsHelperText, setErrorItemsHelperText] = useState("")
    const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const setCheckboxValue = (name: string, value: string) => {
        const temp = values[name] ? JSON.parse(JSON.stringify(values[name])) : [];
        const ind = temp.indexOf(value);
        if (ind > -1) temp.splice(ind, 1)
        else temp.push(value)
        setValues(state => ({
            ...state,
            [name]: temp
        }))
    }

    const handleTriggerOnChange = (data: GENERAL_OBJECT, triggerOnChange?: boolean, triggerForValuesOf?: string[] | null) => {
        if (triggerOnChange) {
            let doTrigger = true;
            if (triggerForValuesOf?.length) {
                for (let key of triggerForValuesOf) {
                    if (doTrigger) {
                        doTrigger = !!values[key]
                    }
                }
            }
            if (doTrigger) {
                if (onValidSubmit) {
                    onValidSubmit(data);
                }
            }
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, type?: string, triggerOnChange?: boolean, triggerForValuesOf?: string[] | null) => {
        if (!apiLoading) {
            const {name, value} = e.target;
            if (type === "checkbox") {
                setCheckboxValue(name, value)
            } else {
                const tempItem = formItems.flatMap(formItem => formItem.items).find(item => item.name === name);
                if (tempItem) {
                    let val: string = value;
                    if(tempItem.max) {
                        if(tempItem.type === "number") val = Number(val) > tempItem.max ? values[name] : val;
                        if(tempItem.type === "text") val = val.length > tempItem.max ? values[name] : val;
                    }
                    val = tempItem.onlyAlphabets ? val.replace(/[^a-zA-Z ]/g, "") : val
                    setValues({
                        ...values,
                        [name]: val
                    });
                    handleTriggerOnChange({
                        ...values,
                        [name]: val
                    }, triggerOnChange, triggerForValuesOf)
                    if(tempItem.onChange) {
                        tempItem.onChange({
                            name,
                            val
                        })
                    }
                }
            }
            setErrorItems([]);
            setErrorItemsHelperText("");
        }
    }

    const combineItems = () => {
        let allItems: FORM_ITEM[] = [];
        for (let formItem of formItems) {
            if (formItem?.items?.length) {
                allItems = allItems.concat(formItem.items);
            }
        }
        return allItems;
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const allItems = combineItems();
        for (let item of allItems) {
            let isError = false, helperText = "";
            const value = values[item.name];
            const validationType = item.validationType ?? item.type;
            if (!value && item.validation) isError = true;
            if (item.throwErrorForValue) {
                if (value === item.throwErrorForValue.value) {
                    isError = true;
                    helperText = item.throwErrorForValue.message ?? "";
                }
            }
            if (item.validationType && !!value) {
                if (validationType === "email") {
                    isError = !(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value as string))
                    helperText = t("invalidEmailFormat" as any)
                } else if (validationType === "date") {
                    if (!isValidDate(value)) {
                        isError = true;
                        helperText = t("invalidDateFormat" as any)
                    } else {
                        if (item.validationData) {
                            const res = isDateIntervalValid(item, value)
                            if (res.code === 500) {
                                isError = true;
                                helperText = t(res.message as any, res.localesData)
                            }
                        }
                    }
                } else if (validationType === "checkbox") {
                    if (item.minItems && value?.length < item.minItems) {
                        isError = true;
                        helperText = t("minimumItemsToBeSelected" as any, {num: item.minItems})
                    }
                    if (item.maxItems && value?.length < item.maxItems) {
                        isError = true;
                        helperText = t("maximumItemsToBeSelected" as any, {num: item.maxItems})
                    }
                } else if (item.min || item.max) {
                    if (validationType === "number") {
                        const minError = Boolean(item.min) && Number(value) < item.min!;
                        const maxError = Boolean(item.max) && Number(value) > item.max!;
                        isError = false;
                        helperText = minError ? t("numberMustBeGreaterThan", {min: item.min}) : t("numberMustBeLessThan", {max: item.max})
                    }
                    if (validationType === "text") {
                        const minError = Boolean(item.min) && value.length < item.min!;
                        const maxError = Boolean(item.max) && value.length > item.max!;
                        isError = false;
                        helperText = minError ? t("minimumNumberOfCharactersMustBe", {min: item.min}) : t("maximumNumberOfCharactersMustBe", {max: item.max})
                    }
                }
            }
            if (isError) {
                setErrorItems([item.name!])
                setErrorItemsHelperText(helperText)
                fieldRefs.current[item.name]?.scrollIntoView({behavior: "smooth", block: "center"});
                return;
            }
        }
        if (onValidSubmit) {
            onValidSubmit(values);
        }
    }

    return <Grid className="main-container df he1 fdc" sx={{pb: (isForFilters || isForDialog) ? 0 : 5}}>
        <Box className="df we1 fdc aic" component="form" onSubmit={handleSubmit} sx={{
            "& .react-tel-input .form-control": {
                width: "100%",
                transition: "border-color 0.3s",
                "&:hover": {borderColor: `${theme.palette.primary.main} !important`}, // Hover (Primary Color)
                "&:focus": {borderColor: `${theme.palette.primary.main} !important`}, // Focus (Primary Color)
                "&.error": {borderColor: `${theme.palette.error.main} !important`}, // Error State
            },
        }}>
            {formItems.map((formItem, index) => {
                return <Grid key={`formItem-${index}`} className="df br5 fdc" container
                              sx={{
                                  ...(formItemGlobalStyles ?? {}), ...(formItem.styles ?? {}),
                                  // @ts-ignore
                                  backgroundColor: formItem.noBackgroundColor ? null : "backgrounds.form",
                                  pb: isForFilters ? 0 : 3
                              }}>
                    {!!formItem.label && <Grid className="df we1 fs_2 fdc" sx={theme => ({
                        color: "texts.heading",
                        p: 1.5,
                        // @ts-ignore
                        borderBottom: `1px solid ${theme.palette?.borders?.formDivider}`,
                        fontWeight: "bold"
                    })}>{formItem.label}
                        <Typography color="texts.main" variant="caption">{formItem.description}.</Typography>
                    </Grid>}
                    <Grid container className="we1" sx={{pl: isForFilters ? 0 : 2, pr: isForFilters ? 0 : 2}}>
                        {formItem.items.map((item: FORM_ITEM, groupIndex: number) => {
                            if (Boolean(item.hide)) return null;
                            const CommonDateComponent = item.type === "date" ? DatePicker : DateTimePicker;
                            const sharedProps = {
                                sx: {
                                    backgroundColor: "backgrounds.main",
                                    mt: 0.1,
                                    ...(formItem.globalStyles ?? {}),
                                    ...(item.localStyles ?? {})
                                },
                                value: values[item.name],
                                onChange: (newDate: any) => {
                                    if (!loading) {
                                        setValues({...values, [item.name]: newDate});
                                        handleTriggerOnChange({
                                            ...values,
                                            [item.name]: newDate
                                        }, item.triggerOnChange, item.triggerForValuesOf)
                                    }
                                },
                                label: item.inlineLabel ? item.label : "",
                                disabled: Boolean(item?.disabled),
                                disableFuture: !!item.shouldDisableFuture,
                                disablePast: !!item.shouldDisablePast,
                                minDate: item.minDate ?? undefined,
                                format: "DD/MM/YYYY"
                            };
                            if (item.type === "label") {
                                return <Grid container className="df we1 fdc">
                                    <Grid sx={{
                                        p: 1,
                                        pb: 0,
                                        color: "texts.heading",
                                        fontWeight: "bold", ...(item.labelStyles ?? {})
                                    }}>
                                        <SafeHtml html={item.label}/>
                                    </Grid>
                                    {Boolean(item.name) && <Grid sx={{p: 1, pt: 0, pb: 0}}>
                                        <Grid sx={{...(item.nameStyles ?? {})}}>
                                            <Typography variant="subtitle2"><SafeHtml html={item.name}/></Typography>
                                        </Grid>
                                    </Grid>}
                                </Grid>
                            }
                            return <>
                                {!hidden.includes(item.name) ? <Grid key={`${item.name}-${index}-${groupIndex}`}
                                                                      size={item.size ? item.size : formItem.size ? formItem.size : {}}
                                                                      sx={{...(formItem.groupStyles ?? {p: 1})}}>
                                    {loading && <CustomSkeleton howMany={1} type="text"/>}
                                    {!loading && <Grid
                                        className="df fdc we1"
                                        key={`formItem-${item.name}`}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: !item.inlineLabel ? !item.multiline ? "2.8rem" : item.height : null
                                            }
                                        }}
                                    >
                                        {(!item.inlineLabel && Boolean(item.label)) && <Typography
                                            variant="subtitle2"
                                            sx={{
                                                mt: 1,
                                                ...(item.localLabelStyles ?? {}),
                                                ...(globalLabelStyles ?? {})
                                            }}
                                        >
                                            <div style={{display: "flex"}}>
                                                <SafeHtml html={item.label}/>
                                                {!!item.validation && <span
                                                    style={{
                                                        color: 'red',
                                                        marginLeft: "2px",
                                                        marginRight: "2px"
                                                    }}>*</span>}
                                            </div>
                                        </Typography>}
                                        {["text", "number", "email", "search"].includes(item.type) ?
                                            // @ts-ignore
                                            <Grid className="df we1 fdc"
                                                   ref={(el) => (fieldRefs.current[item.name] = el)}>
                                                <TextField
                                                    label={item.inlineLabel ? item.label : ""}
                                                    id={`text-${index}`}
                                                    name={item.name}
                                                    multiline={Boolean(item.multiline)}
                                                    maxRows={Boolean(item.maxRows) ? item.maxRows : undefined}
                                                    variant={item.variant ?? "outlined"}
                                                    disabled={Boolean(item?.disabled)}
                                                    value={values[item.name] ?? ""}
                                                    // @ts-ignore
                                                    onChange={handleChange}
                                                    type={item.type === "search" ? "text" : item.type}
                                                    sx={{
                                                        backgroundColor: "backgrounds.main",
                                                        mt: 0.1,
                                                        ...(globalStyles ?? {}), ...(item.localStyles ?? {})
                                                    }}
                                                    // @ts-ignore
                                                    InputProps={item.type === "search" ? {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon/>
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: values[item.name] && (
                                                            <InputAdornment position="end">
                                                                <IconButton size="small" onClick={() => {
                                                                    setValues({
                                                                        ...values,
                                                                        [item.name]: ""
                                                                    });
                                                                    if (item.triggerOnReset) {
                                                                        if (onValidSubmit) {
                                                                            onValidSubmit({
                                                                                ...values,
                                                                                [item.name]: ""
                                                                            })
                                                                        }
                                                                    }
                                                                }}>
                                                                    <ClearIcon fontSize="small"/>
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    } : null}
                                                />
                                            </Grid> : item.type === "select" ?
                                                // @ts-ignore
                                                <Grid className="df we1 fdc"
                                                       ref={(el) => (fieldRefs.current[item.name] = el)}>
                                                    <CustomSelect
                                                        name={item.name}
                                                        label={item.inlineLabel ? item.label : ""}
                                                        value={values[item.name]}
                                                        onChange={handleChange}
                                                        options={item.options!}
                                                        triggerOnChange={item.triggerOnChange}
                                                        triggerForValuesOf={item.triggerForValuesOf}
                                                    />
                                                </Grid> : ["date", "dateTime"].includes(item.type) ?
                                                    // @ts-ignore
                                                    <Grid className="df we1 fdc"
                                                           ref={(el) => (fieldRefs.current[item.name] = el)}>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <CommonDateComponent {...sharedProps} />
                                                        </LocalizationProvider>
                                                    </Grid> : item.type === "contact" ?
                                                        // @ts-ignore
                                                        <Grid className="df we1 fdc" sx={{
                                                            "& .myClass": {}
                                                        }} ref={(el) => (fieldRefs.current[item.name] = el)}>
                                                            <PhoneInput
                                                                inputClass={"myclass"}
                                                                searchNotFound={t("noRecordFound")}
                                                                countryCodeEditable={false}
                                                                enableSearch={true}
                                                                onlyCountries={item.countries ?? ['pk']}
                                                                specialLabel=""
                                                                placeholder={item.placeHolder ?? t("enterPhoneNumber")}
                                                                country={item.defualtCountry ?? 'pk'}
                                                                value={values[item.name] ?? ""}
                                                                onChange={(phone, country: CountryData) => {
                                                                    if (!apiLoading) {
                                                                        setValues(state => ({
                                                                            ...state,
                                                                            [item.name]: phone,
                                                                            [`${item.name}-pre`]: country.dialCode
                                                                        }))
                                                                        setErrorItems([]);
                                                                        setErrorItemsHelperText("");
                                                                    }
                                                                }}
                                                                containerStyle={{width: "100%"}}
                                                                inputStyle={{
                                                                    width: "100%",
                                                                    height: "2.8rem",
                                                                    backgroundColor: theme.palette?.backgrounds?.main,
                                                                    color: theme.palette?.text?.primary,
                                                                    border: `1px solid ${theme.palette?.borders?.contactInput}`
                                                                }}
                                                            />
                                                        </Grid> : ["groupCheckboxes", "groupRadioBoxes"].includes(item.type) ?
                                                            // @ts-ignore
                                                            <Grid className="df we1 fdc"
                                                                   ref={(el) => (fieldRefs.current[item.name] = el)}>
                                                                <FormGroup>
                                                                    <Grid container spacing={2}>
                                                                        {item.options!.map((option) => (
                                                                            <Grid
                                                                                size={item.rowSize ?? {
                                                                                    xs: 12,
                                                                                    md: 3,
                                                                                    lg: 2
                                                                                }}
                                                                                key={`${option.value}-${index}-${option.value}`}>
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        item.type === "groupCheckboxes" ?
                                                                                            <Checkbox
                                                                                                checked={values[item.name]?.length ? values[item.name].includes(`${option.value}`) : false}
                                                                                                onChange={(event) => handleChange(event, "checkbox")}
                                                                                                name={item.name}
                                                                                                value={option.value}
                                                                                            /> : <Radio
                                                                                                checked={values[item.name] == option.value}
                                                                                                onChange={(event) => handleChange(event, "radio")}
                                                                                                name={item.name}
                                                                                                value={option.value}
                                                                                            />
                                                                                    }
                                                                                    label={option.label}
                                                                                />
                                                                            </Grid>
                                                                        ))}
                                                                    </Grid>
                                                                </FormGroup>
                                                            </Grid> : null}
                                        {errorItems.includes(item.name) &&
                                            <SafeHtmlRenderer
                                                errorItemsHelperText={errorItemsHelperText}
                                                helperText={item.helperText}
                                            />}
                                    </Grid>}
                                </Grid> : null}
                            </>
                        })}
                    </Grid>
                </Grid>
            })}

            {!!error && <Grid container className="df we1 jcc">
                <Grid className="df we1 jce" sx={{...(formItemGlobalStyles ?? {})}}>
                    <Error error={error} timeout={10000}/>
                </Grid>
            </Grid>}

            {!loading &&
                <Grid container className="df we1 jcc"
                       sx={{...(buttonStyles ?? {}), pl: isForFilters ? 0.5 : 0, pr: isForFilters ? 0.5 : 0}}>
                    <Grid className={`df we1 ${isForFilters ? "" : "jce"}`} sx={{...(formItemGlobalStyles ?? {})}}>
                        {!!cancelButton && <Grid sx={{p: 0.5}}>
                            <Button
                                startIcon={!cancelButton?.icon ? null : <Icon>{cancelButton.icon}</Icon>}
                                disabled={apiLoading}
                                sx={{width: "8rem", ...button}}
                                variant="outlined"
                                // @ts-ignore
                                component={!cancelButton?.link ? null : Link}
                                href={!cancelButton?.link ? null : cancelButton.link}
                                type="button"
                                onClick={!cancelButton?.onClick ? null : cancelButton.onClick}
                            >{cancelButton?.label ?? t("global:cancel")}</Button>
                        </Grid>}
                        {Boolean(customButtons) && customButtons.map((bt: any) => {
                            return <Grid sx={{p: 0.5}}>
                                <LoadingButton
                                    startIcon={!bt?.icon ? null : <Icon>{bt.icon}</Icon>}
                                    disabled={Boolean(bt?.disabled)}
                                    sx={{width: "8rem", ...(bt?.styles ?? {})}}
                                    variant="outlined"
                                    // @ts-ignore
                                    component={!bt?.link ? null : Link}
                                    href={!bt?.link ? null : bt.link}
                                    type="button"
                                    onClick={!bt?.onClick ? null : bt.onClick}
                                    loading={Boolean(bt?.loading)}
                                >{bt?.label ?? t("global:submit")}</LoadingButton>
                            </Grid>
                        })}
                        {!noSubmitButton && <Grid sx={{p: 0.5}}>
                            <LoadingButton
                                disabled={disableSubmitButton}
                                startIcon={<Icon>publish</Icon>}
                                loading={apiLoading}
                                sx={{width: "8rem"}}
                                variant="outlined"
                                type="submit"
                            >{t(isForFilters ? "global:apply" : "global:submit")}</LoadingButton>
                        </Grid>}
                    </Grid>
                </Grid>}

        </Box>
    </Grid>
}

export default CustomFormNew;